import { Navigate } from "react-router-dom";
import { ShieldQuestion } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
} from "@eduviaos/ui-kit";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useTerms } from "@/hooks/useTerms";
import { StaffTab } from "./StaffTab";

/**
 * Study-centre team roster. Learners stay on `/people`. Schools keep
 * staff as a tab there, so this route sends them back.
 */
export function TeamPage() {
  const { user } = useAuth();
  const { vertical, isLoading } = useEnabledModules();
  const { permissionLabel } = useTerms(vertical ?? "study_centre");

  if (!isLoading && vertical && vertical !== "study_centre") {
    return <Navigate to="/people?tab=staff" replace />;
  }

  const canViewStaff = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "view_staff",
        user.permission_mode ?? "additive",
      )
    : false;
  const canManageStudents = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_students",
        user.permission_mode ?? "additive",
      )
    : false;
  const canManageStaff = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_staff",
        user.permission_mode ?? "additive",
      )
    : false;
  const canManageAccess = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_roles",
        user.permission_mode ?? "additive",
      )
    : false;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Team</PageHeaderHeading>
            <PageHeaderDescription>
              People who run the centre. Learners are on their own page.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>
      {canViewStaff ? (
        <StaffTab
          canManage={canManageStudents}
          canManageStaff={canManageStaff}
          canManageAccess={canManageAccess}
          currentUserId={user?.id ?? null}
          tenantSlug={user?.tenant_id ?? null}
          vertical={vertical ?? "study_centre"}
        />
      ) : (
        <Card className="border-border/80 bg-card/60" data-testid="team-permission-denied">
          <CardHeader className="flex-col items-start gap-3 pb-4">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="bg-muted text-muted-foreground grid size-12 place-items-center rounded-xl"
              >
                <ShieldQuestion className="size-6" />
              </span>
              <div>
                <CardTitle className="text-xl tracking-tight">No access yet</CardTitle>
                <CardDescription className="mt-1 text-xs">
                  You don&apos;t have the &quot;{permissionLabel("view_staff")}&quot; permission
                  yet.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Ask an admin to give you &quot;{permissionLabel("view_staff")}&quot; to see the team.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
